import type { ActivityEvent, Branch, DailySale } from './domain';

const DB_NAME = 'daily-sales-v1';
const DB_VERSION = 1;
const pendingWrites = new Set<Promise<unknown>>();

function trackWrite<T>(promise: Promise<T>): Promise<T> {
  pendingWrites.add(promise);
  void promise.then(
    () => pendingWrites.delete(promise),
    () => pendingWrites.delete(promise)
  );
  return promise;
}

export async function waitForPendingWrites(): Promise<void> {
  while (pendingWrites.size) await Promise.allSettled([...pendingWrites]);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('branches')) db.createObjectStore('branches', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('sales')) {
        const store = db.createObjectStore('sales', { keyPath: 'id' });
        store.createIndex('date', 'salesDate');
        store.createIndex('dateBranch', ['salesDate', 'branchId'], { unique: true });
      }
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'eventId' });
      if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAll<T>(storeName: string): Promise<T[]> {
  const db = await openDb();
  return requestValue(db.transaction(storeName).objectStore(storeName).getAll()) as Promise<T[]>;
}

export async function putValue<T>(storeName: string, value: T): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).put(value);
  await trackWrite(new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); }));
}

export async function deleteValue(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).delete(key);
  await trackWrite(new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); }));
}

export async function clearStore(storeName: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).clear();
  await trackWrite(new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); }));
}

export const loadLocalState = async () => ({
  branches: await getAll<Branch>('branches'),
  sales: await getAll<DailySale>('sales'),
  events: await getAll<ActivityEvent>('outbox')
});

export async function setting(key: string): Promise<string | undefined> {
  const db = await openDb();
  return requestValue(db.transaction('settings').objectStore('settings').get(key));
}

export async function setSetting(key: string, value: string) {
  const db = await openDb();
  const tx = db.transaction('settings', 'readwrite');
  tx.objectStore('settings').put(value, key);
  await trackWrite(new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); }));
}
